/* eslint-disable camelcase */
/* eslint-disable react/prop-types */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { Grid, Typography, IconButton, Dialog, Divider, Box, Tooltip } from '@mui/material';
import { CancelPresentationRounded, ContentCopyOutlined, DeleteRounded, FormatBold, PersonAddAlt, SaveRounded } from '@mui/icons-material';
import PropTypes from 'prop-types';
import styles from './style';
import Datagrid from '../../ui/Datagrid';
import columns from './table_config';
import { renderCell } from '../../ui/Datagrid/table_config';
import { createDrillStringDetailsProperty, deleteDrillStringDetailsProperty, listDrillString, updateDrillStrigPosition, updateDrillStringDetailsProperty } from '../../../store/actions/wellsDrillString';
import { enqueueSnackbar } from '../../../store/actions/snackbar';
import { APP_ACTION_TYPES, HYDRAULICS_ADMIN_ROLES, PERMISSION_DIALOG_SOURCES, TOOLTIPS, WELLS_DRILL_STRING_VALIDATION } from '../../../utils/constants';
import SelectEditInputTextField from '../../ui/SelectEditInputTextField';
import DeleteConfirmationModal from '../../deleteConfirmationModal';
import useUnsavedChanges from '../../../hooks/useUnsavedChanges';
import useUnitConversion from '../../../hooks/useUnitConversion';
import { UNIT_SYSTEM } from '../../../shared/config/hydraulics_constants';
import { COLORS } from '../../../shared/config/colors';
import DrillStringBitDetails from '../drillStringBit';
import _ from 'lodash';
import { modifyMetricData } from '../../../utils/getMetrics';
import PermissionsModal from '../../permissions';
import { checkPermissions } from '../../../utils/checkPermissions';
import CreatedByInfoDialog from '../../ui/CreatedByInfoDialog';

function generateRandom() {
  const length = 24;
  const charset = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let retVal = '';
  for (let i = 0, n = charset.length; i < length; ++i) {
    retVal += charset.charAt(Math.floor(Math.random() * n));
  }
  return retVal;
}

const DrillStringDetails = ({
  handleClose,
  data,
  open,
  drillStringData
}) => {
  const dispatch = useDispatch();
  const classes = styles();
  const apiRef = useGridApiRef();
  const drillString = useSelector((state) => state.wellsDrillString);
  const [properties, setProperties] = useState([]);
  const [invalidRows, setInvalidRows] = useState({});
  const [deleteAction, setDeleteAction] = useState(false);
  const [deleteRow, setDeleteRow] = useState();
  const { unit, convertUnitSystem } = useUnitConversion();
  const {
    unSavedExists,
    handleClose: unSavedDialogClose,
    UnsavedChangesModal
  } = useUnsavedChanges(properties, handleClose);
  const [showBITDialog, setShowBITDialog] = useState(false);
  const disabledAction = drillString.loading || properties.some(item => item?.isDirty === true || item?.isNewRow);
  const rowData = drillString?.data?.find(item => item._id === data?._id);
  const bitDetails = rowData?.bit_details;
  const user = useSelector((state) => state.currentUser);
  const [permissions, setPermissions] = useState({
    open: false,
    data: {}
  });
  const isHydraulicsAdmin = HYDRAULICS_ADMIN_ROLES.includes(user?.type);

  useEffect(() => {
    if (!drillString?.loading) {
      const drillStringDetails = drillString?.data?.find(item => item._id === data?._id)?.drillstring_details;
      if (drillStringDetails) {
        let runningTotal = 0;
        const modifiedDrillStringDetailsData = modifyMetricData(drillStringDetails, drillString?.units?.details, unit);
        const updatedDetails = modifiedDrillStringDetailsData.map((item, index) => {
          const len = Number(item?.len) || 0;
          runningTotal += len;
          return {
            ...item,
            rowNumber: index + 1,
            cum_len: runningTotal,
          };
        });
        setProperties(updatedDetails);
      }
      return () => {
        setProperties([]);
      };
    }
  }, [drillString]);

  useEffect(() => {
    if (properties[0]?.name === '') {
      const id = properties[0]?._id;
      setTimeout(() => {
        apiRef.current.scrollToIndexes({
          rowIndex: 1
        });
        apiRef.current.setCellMode(id, 'name', 'edit');
        setTimeout(() => {
          apiRef.current.setCellFocus(id, 'name');
        }, 50);
      }, 50);
      apiRef.current.setPage(0);
    }
  }, [properties.length]);

  const handleAddProperty = (row = null) => {
    const currentProperties = [...properties];
    const nextId = currentProperties.length - 1;
    const newRow = {
      name: row?.name ?? '',
      od: row?.od ?? '',
      id: row?.id ?? '',
      tj_od: row?.tj_od ?? 0,
      tj_id: row?.tj_id ?? 0,
      len: row?.len ?? '',
      'tj_len%': row['tj_len%'] ?? 0,
      grid_id: nextId + 1,
      _id: generateRandom(),
      isNewRow: true,
      isDirty: true
    };
    currentProperties.unshift(newRow);
    setProperties(currentProperties);
  };

  const checkDrillStringValues = (value) => {
    const row = value;
    const invalidFields = [];
    if (!row?.name) invalidFields.push('Name');
    if (row?.od === undefined || row?.od === null || row?.od === '') invalidFields.push('OD');
    if (row?.id === undefined || row?.id === null || row?.id === '') invalidFields.push('ID');
    if (row?.tj_od === undefined || row?.tj_od === '') invalidFields.push('Tooljoint OD');
    if (row?.tj_id === undefined || row?.tj_id === '') invalidFields.push('Tooljoint ID');
    if (row?.len === undefined || row?.len === '') invalidFields.push('Length');
    if (row['tj_len%'] === undefined || row['tj_len%'] === '') invalidFields.push('Tooljoint Length %');

    if (invalidFields.length === 0) {
      setInvalidRows(prev => ({ ...prev, [row._id]: false }));
      return true;
    } else {
      setInvalidRows(prev => ({ ...prev, [row._id]: true }));
      dispatch(
        enqueueSnackbar(
          `Please enter the following details: ${invalidFields.join(', ')}`,
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    }
  };

  const validateProperties = (row) => {
    const { name, od, id, tj_od, tj_id, len, 'tj_len%': tj_len } = row;
    const minId = drillString?.units?.details?.id?.min;
    if (typeof name !== 'string' || name.length > 100) return WELLS_DRILL_STRING_VALIDATION.name;
    if (typeof od !== 'number' || od <= id) return WELLS_DRILL_STRING_VALIDATION.od;
    if (typeof id !== 'number' || id < minId) return `ID should be a number greater than or equal to ${minId} ${drillString?.units?.details?.id?.label}`;
    if (typeof tj_od !== 'number' || tj_od < 0.0) return WELLS_DRILL_STRING_VALIDATION.tj_od;
    if (typeof tj_id !== 'number' || tj_id < 0.0) return WELLS_DRILL_STRING_VALIDATION.tj_id;
    if (typeof len !== 'number' || len <= 0.0) return WELLS_DRILL_STRING_VALIDATION.len;
    if (typeof tj_len !== 'number' || tj_len < 0 || tj_len > 15) return WELLS_DRILL_STRING_VALIDATION.tj_len;

    if (tj_len !== 0) {
      if (tj_od < od) return WELLS_DRILL_STRING_VALIDATION.tj_len_non_zero_tj_od;
      if (tj_id < 1.0) return WELLS_DRILL_STRING_VALIDATION.tj_len_min_tj_id;
    }

    return '';
  };

  const saveProperty = (row) => {
    if (checkDrillStringValues(row)) {
      for (const key in row) {
        if (typeof row[key] === 'string' && !isNaN(row[key])) {
          row[key] = Number(row[key]);
        }
      }
      const validationError = validateProperties(row);
      if (validationError === '') {
        const body = {
          name: row.name,
          od: unit !== UNIT_SYSTEM.US ? convertUnitSystem(row.od, drillString?.units?.details?.od?.conversionFactor) : row.od,
          id: unit !== UNIT_SYSTEM.US ? convertUnitSystem(row.id, drillString?.units?.details?.id?.conversionFactor) : row.id,
          tj_od: unit !== UNIT_SYSTEM.US ? convertUnitSystem(row.tj_od, drillString?.units?.details?.tj_od?.conversionFactor) : row.tj_od,
          tj_id: unit !== UNIT_SYSTEM.US ? convertUnitSystem(row.tj_id, drillString?.units?.details?.tj_id?.conversionFactor) : row.tj_id,
          len: unit !== UNIT_SYSTEM.US ? convertUnitSystem(row.len, drillString?.units?.details?.len?.conversionFactor) : row.len,
          'tj_len%': row['tj_len%'],
        };
        if (row?.isNewRow) {
          dispatch(createDrillStringDetailsProperty(data?._id ? data?._id : drillString.drillStringId, body, data.wellsInfoId));
        } else {
          dispatch(updateDrillStringDetailsProperty(data?._id ? data?._id : drillString.drillStringId, row?._id, body, data.wellsInfoId));
        }
        Object.keys(row).forEach((field) => {
          apiRef.current.setCellMode(row._id, field, 'view');
        });
      } else {
        if (validationError.length) setInvalidRows(prev => ({ ...prev, [row._id]: true }));
        dispatch(enqueueSnackbar(validationError, 'error', new Date().getTime() + Math.random()));
      }
    }
  };

  const updateData = (id, field, value) => {
    setProperties(prevProperties => {
      const updatedProperties = prevProperties.map(item => {
        if (item._id === id) {
          const isDirty = value !== item[field];
          return {
            ...item,
            [field]: typeof value === 'string' ? value : Number(value),
            isDirty: item.isDirty || isDirty,
          };
        }
        return item;
      });
      return updatedProperties;
    });
  };

  const handleDeleteDrillString = (row) => {
    const filteredProperties = properties.filter((item) => item._id !== row._id).filter((item) => !item.isNewRow);
    setProperties(filteredProperties);
    if (!row?.isNewRow) {
      dispatch(deleteDrillStringDetailsProperty(data?._id ? data?._id : drillString.drillStringId, row?._id, data.wellsInfoId));
    }
    setDeleteAction(false);
    setDeleteRow({});
  };

  const renderValueCell = (params) => {
    return <SelectEditInputTextField numberFormatOnly={params?.field !== 'name'} {...params} updateData={updateData} />;
  };

  const renderActions = ({ row }) => {
    return (
      <Grid container justifyContent="center">
        <IconButton
          variant="solid"
          disabled={properties.some(item => item?.isDirty === true || item?.isNewRow) || (!row.isNewRow && !checkPermissions(APP_ACTION_TYPES.CREATE, rowData?.permissions))}
          onClick={() => {
            handleAddProperty(row);
          }}
        >
          <ContentCopyOutlined />
        </IconButton>
        <IconButton
          variant="solid"
          fontSize="large"
          disabled={!row.isDirty || drillString.loading || (!row.isNewRow && !checkPermissions(APP_ACTION_TYPES.UPDATE, rowData?.permissions))}
          onClick={() => {
            saveProperty(row);
          }}
        >
          <SaveRounded />
        </IconButton>
        <IconButton
          disabled={drillString.loading || (!row.isNewRow && !checkPermissions(APP_ACTION_TYPES.DELETE, rowData?.permissions))}
          variant="solid"
          onClick={() => {
            setDeleteAction(true);
            setDeleteRow(row);
          }}
        >
          <DeleteRounded />
        </IconButton>
      </Grid>
    );
  };

  const closeHandler = () => {
    if (unSavedExists) {
      unSavedDialogClose(false);
      return;
    }
    setProperties([]);
    handleClose();
  };
  const handleRowOrderChange = async (params) => {
    if (params?.oldIndex !== params?.targetIndex) {
      const body = {
        position: params?.targetIndex
      };
      dispatch(updateDrillStrigPosition(data?._id, params?.row?._id, body, data.wellsInfoId));
      Object.keys(params?.row).forEach((field) => {
        apiRef.current.setCellMode(params?.row._id, field, 'view');
      });
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!drillString.loading) {
          closeHandler();
        }
      }}
      className={classes.dialogModal}
      maxWidth={'lg'}
      fullWidth={true}
    >
      <div className={classes.dialogTitle}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '1.3rem' }}>
          <Grid container alignItems="center" justifyContent="space-between">
            <Grid item>{drillStringData?.row?.name?.trim()}, {data?.name || ''} {data?.name ? '@' : ''} {data.MD.toFixed(drillString?.units?.MD?.decimals)} {drillString?.units?.MD?.label} <CreatedByInfoDialog data={rowData} loading={disabledAction} /></Grid>
            <Grid item display='flex' alignItems='center'>
              <Box>
                <Tooltip
                  title={TOOLTIPS.drillingBit}
                   slotProps={{
                     tooltip: {
                       sx: {
                         fontSize: '14px'
                       },
                     },
                   }}
                  >
                  <span>
                    <IconButton
                      variant="solid"
                      disabled={disabledAction}
                      sx={{ color: !_.isEmpty(bitDetails) && 'green', mt: '2px' }}
                      onClick={() => {
                        setShowBITDialog(true);
                      }}
                    >
                      <FormatBold fontSize="large" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
                 {isHydraulicsAdmin
                   ? (
                  <Tooltip title={TOOLTIPS.viewPermissions}>
                    <span>
                      <IconButton
                        variant="solid"
                        key="permissions"
                        disabled={disabledAction}
                        sx={{ color: rowData?.permissionId && 'green' }}
                        onClick={() => {
                          setPermissions({
                            open: true,
                            data: {
                              ...rowData,
                              permissionId: rowData?.permissionId ?? null
                            }
                          });
                        }}
                      >
                        <PersonAddAlt fontSize='large' />
                      </IconButton>
                    </span>
                  </Tooltip>
                     )
                   : null}
              <IconButton
                disabled={drillString.loading}
                onClick={closeHandler}
              >
                <CancelPresentationRounded fontSize='large' />
              </IconButton>
            </Grid>
          </Grid>
        </Typography>
      </div>
      <Divider />
      <Grid container>
        <Grid item className={classes.customTool} justifyContent="space-between" sx={{ display: !checkPermissions(APP_ACTION_TYPES.CREATE, rowData?.permissions) && 'none !important' }}>
          <Grid item>
            <Typography variant='button' fontWeight='700' className={drillString.loading || properties.some(item => item?.isDirty === true || item?.isNewRow) ? classes.disableIcon : ''} onClick={handleAddProperty}>+ Add</Typography>
          </Grid>
          <Grid item marginLeft='12px' className={classes.entryLabel}>
            <Typography variant='h6' color={COLORS.LIGHT_GREY} fontWeight='500'>{'(Note: from bottom to top; last row extends to surface)'}</Typography>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Datagrid
            apiRef={apiRef}
            data={properties}
            loading={drillString.loading}
            getRowId={(row) => row._id}
            columns={columns(renderCell, renderActions, renderValueCell, drillString?.units?.details, unit)}
            disableStickyHeader={false}
            autoHeight={false}
            rowReordering={true}
            onRowOrderChange={handleRowOrderChange}
            height={'50vh'}
            sx={{
              '.MuiDataGrid-cell': {
                fontSize: '1rem',
                fontWeight: 900
              },
              '.MuiDataGrid-columnHeader': {
                fontSize: '1.2rem',
                fontWeight: '900 !important'
              },
              '.MuiDataGrid-rowReorderCell': {
                opacity: properties[0]?.isNewRow ? 0.38 : '',
                pointerEvents: properties[0]?.isNewRow ? 'none' : '',
              },
            }}
            pageSize={100}
            editMode="cell"
            toolbar={{
              options: {
                columns: false,
                filters: false,
                density: false,
                export: false
              },
            }}
            onCellEditCommit={({ id, value, field }) => {
              updateData(id, field, value);
            }}
            getRowClassName={(params) => {
              return invalidRows[params.id] ? classes.invalidRow : '';
            }}
            enableDirtyLock
            permissions={rowData?.permissions}
          />
        </Grid>
        {
          deleteAction && <DeleteConfirmationModal
            open={deleteAction}
            handleDelete={handleDeleteDrillString}
            handleClose={setDeleteAction}
            deleteRow={deleteRow}
          />
        }
        <UnsavedChangesModal />
      </Grid>
      {showBITDialog &&
        <DrillStringBitDetails
          drillStringData={drillStringData}
          open={showBITDialog}
          handleClose={() => {
            setShowBITDialog(false);
          }}
          data={data}
        />}
      {permissions?.open
        ? (
          <PermissionsModal
            isOpen={permissions?.open}
            data={
              {
                ...permissions?.data,
                collection: PERMISSION_DIALOG_SOURCES?.wellsDrillStrings,
              }
            }
            onCloseClick={() => {
              setPermissions({
                open: false,
                data: {}
              });
            }}
            refreshParentDialog={() => {
              dispatch(listDrillString(drillStringData?.row?._id));
            }}
          />
          )
        : null}
    </Dialog>
  );
};
DrillStringDetails.propTypes = {
  handleClose: PropTypes.func.isRequired,
  data: PropTypes.object,
  open: PropTypes.bool.isRequired,
  drillStringData: PropTypes.any
};
DrillStringDetails.defaultProps = {
  data: {}
};
export default DrillStringDetails;
